import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "./api";
import { useAuth } from "./AuthContext";

export default function UsersPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ email: "", name: "", role: "viewer", password: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/users", {
        ...form,
        email: form.email.trim().toLowerCase(),
      });
      setForm({ email: "", name: "", role: "viewer", password: "" });
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove user ${u.email}?`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      await load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  if (user?.role !== "admin") {
    return (
      <div style={S.page}>
        <div style={{ ...S.card, color: "#fca5a5" }}>
          Admin role required to manage users.
        </div>
      </div>
    );
  }

  return (
    <div style={S.page} data-testid="admin-users-page">
      <header style={S.header}>
        <div>
          <div style={S.brand}>Senior-team users</div>
          <div style={S.subbrand}>Up to 3 seats · admin role can edit Master Rules</div>
        </div>
        <button onClick={() => nav("/admin")} style={S.linkBtn} data-testid="admin-users-back">
          ← Back to rules
        </button>
      </header>

      <section style={S.card}>
        <h3 style={S.h3}>Current users ({users.length}/3)</h3>
        {loading ? (
          <div style={{ color: "#64748b" }}>Loading…</div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Email</th>
                <th style={S.th}>Name</th>
                <th style={S.th}>Role</th>
                <th style={S.th}>Created</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={S.tr} data-testid={`admin-user-row-${u.email}`}>
                  <td style={S.td}>{u.email}</td>
                  <td style={S.td}>{u.name}</td>
                  <td style={S.td}>
                    <span style={{ ...S.rolePill, background: u.role === "admin" ? "#064e3b" : "#1e293b", color: u.role === "admin" ? "#a7f3d0" : "#cbd5e1" }}>
                      {u.role}
                    </span>
                  </td>
                  <td style={{ ...S.td, color: "#64748b", fontSize: 12 }}>
                    {(u.created_at || "").slice(0, 10)}
                  </td>
                  <td style={S.td}>
                    {u.id !== user.id && (
                      <button
                        data-testid={`admin-user-remove-${u.email}`}
                        onClick={() => removeUser(u)}
                        style={{ ...S.smallBtn, color: "#fca5a5" }}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {users.length < 3 && (
        <section style={S.card}>
          <h3 style={S.h3}>Add a new user</h3>
          {err && <div style={S.err}>{err}</div>}
          <form onSubmit={add} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, alignItems: "end" }}>
            <Field label="Email">
              <input
                data-testid="admin-newuser-email"
                type="email" required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                style={S.input}
              />
            </Field>
            <Field label="Name (optional)">
              <input
                data-testid="admin-newuser-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                style={S.input}
              />
            </Field>
            <Field label="Role">
              <select
                data-testid="admin-newuser-role"
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                style={S.input}
              >
                <option value="viewer">Viewer (read-only)</option>
                <option value="admin">Admin (can edit rules + users)</option>
              </select>
            </Field>
            <Field label="Temporary password (min 6 chars)">
              <input
                data-testid="admin-newuser-password"
                type="text" required minLength={6}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                style={S.input}
              />
            </Field>
            <div />
            <button
              data-testid="admin-newuser-submit"
              type="submit"
              disabled={busy}
              style={{ ...S.primary, opacity: busy ? 0.6 : 1 }}
            >
              {busy ? "Adding…" : "Add user"}
            </button>
          </form>
          <div style={{ color: "#64748b", fontSize: 12, marginTop: 12 }}>
            Share the email + temporary password with the new user; they should
            sign in and change it from their browser.
          </div>
        </section>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh", background: "#0b1220", color: "#e2e8f0",
    padding: "20px 24px 48px",
    fontFamily: "ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif",
  },
  header: { display: "flex", justifyContent: "space-between", marginBottom: 18 },
  brand: { fontSize: 18, fontWeight: 700, color: "#f1f5f9" },
  subbrand: { fontSize: 12, color: "#64748b", marginTop: 2 },
  linkBtn: {
    background: "transparent", border: "1px solid #334155",
    color: "#cbd5e1", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13,
  },
  card: {
    background: "#0f172a", border: "1px solid #1f2a44", borderRadius: 10,
    padding: 20, marginBottom: 18,
  },
  h3: { margin: 0, marginBottom: 16, color: "#f1f5f9", fontSize: 15 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left", padding: "8px 10px", color: "#64748b",
    fontSize: 11, letterSpacing: 1, textTransform: "uppercase",
    borderBottom: "1px solid #1f2a44",
  },
  tr: { borderBottom: "1px solid #131c2f" },
  td: { padding: "10px 10px", color: "#e2e8f0" },
  rolePill: { padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600 },
  smallBtn: {
    background: "transparent", border: "1px solid #334155", color: "#cbd5e1",
    padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12,
  },
  input: {
    width: "100%", padding: "9px 12px", borderRadius: 8,
    border: "1px solid #334155", background: "#0b1220",
    color: "#f1f5f9", fontSize: 13, outline: "none", boxSizing: "border-box",
  },
  err: {
    background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.5)",
    color: "#fecaca", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13,
  },
  primary: {
    background: "#10b981", color: "white", border: "none",
    padding: "10px 18px", borderRadius: 8, fontWeight: 700, cursor: "pointer",
  },
};
